import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  TextField,
} from '@mui/material';
import * as MuiIcons from '@mui/icons-material';
import type { OfflinePlayer, TemplateSquareAssignment, CellMode, CellShape } from '../types';
import { getCellForSquare, getSquaresForCell, getTotalCells } from '../types';

// Helper to render an icon by name
const renderIcon = (name: string, size: number = 18) => {
  const IconComponent = (MuiIcons as Record<string, React.ComponentType<{ sx?: object }>>)[name];
  if (!IconComponent) return null;
  return <IconComponent sx={{ fontSize: size }} />;
};

interface PlayerOption {
  id: string;
  displayName: string;
  handle?: string;
  iconName?: string;
}

interface TemplateGridProps {
  assignments: TemplateSquareAssignment[];
  offlinePlayers?: OfflinePlayer[]; // Optional - only needed for dialog-based assignment
  onAssign: (row: number, col: number, playerId: string) => Promise<void>;  // playerId is offlinePlayerId for dialog assignment
  onClear: (row: number, col: number) => Promise<void>;
  assignModeEnabled?: boolean;
  assignModePlayerId?: string;  // For offline player selection
  assignModeConnectionId?: string;  // For connection selection
  onSquareClick?: (row: number, col: number) => void;
  cellMode?: CellMode;
  cellShape?: CellShape;
}

// Get grid dimensions based on cell mode
const getGridDimensions = (cellMode: CellMode, cellShape: CellShape): { rows: number; cols: number } => {
  if (cellMode === 'quad') {
    return { rows: 5, cols: 5 }; // 25 cells in 5x5
  }
  if (cellMode === 'paired') {
    if (cellShape === '2x1') {
      return { rows: 5, cols: 10 }; // 50 cells: 5 row-pairs x 10 columns
    }
    return { rows: 10, cols: 5 }; // 50 cells: 10 rows x 5 column-pairs
  }
  return { rows: 10, cols: 10 }; // 100 cells standard
};

// Get the representative square (top-left) for a cell number
const getCellRepresentativeSquare = (cellNumber: number, cellMode: CellMode, cellShape: CellShape): [number, number] => {
  const squares = getSquaresForCell(cellNumber, cellMode, cellShape);
  return squares[0]; // First square is always top-left
};

export const TemplateGrid: React.FC<TemplateGridProps> = ({
  assignments,
  offlinePlayers = [],
  onAssign,
  onClear,
  assignModeEnabled = false,
  assignModePlayerId,
  assignModeConnectionId,
  onSquareClick,
  cellMode = 'standard',
  cellShape = '1x1',
}) => {
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedCell, setSelectedCell] = useState<number | null>(null);
  const [selectedPlayer, setSelectedPlayer] = useState<PlayerOption | null>(null);

  const totalCells = getTotalCells(cellMode);
  const { rows, cols } = getGridDimensions(cellMode, cellShape);

  // Build a map of cell number to assignment for quick lookup
  // For cell modes, we look up by the first square in the cell
  const cellAssignmentMap = useMemo(() => {
    const map = new Map<number, TemplateSquareAssignment>();
    assignments.forEach((a) => {
      const cellNum = getCellForSquare(a.row, a.col, cellMode, cellShape);
      // Only store if not already stored (first assignment wins)
      if (!map.has(cellNum)) {
        map.set(cellNum, a);
      }
    });
    return map;
  }, [assignments, cellMode, cellShape]);

  const getCellAssignment = (cellNumber: number): TemplateSquareAssignment | undefined => {
    return cellAssignmentMap.get(cellNumber);
  };

  const getCellColor = (cellNumber: number): string => {
    const assignment = getCellAssignment(cellNumber);

    // Highlight cells belonging to the player/connection being assigned in assign mode
    if (assignModeEnabled) {
      if (assignModePlayerId && assignment?.offlinePlayerId === assignModePlayerId) {
        return '#00bcd4'; // Cyan for selected offline player's cells
      }
      if (assignModeConnectionId && assignment?.playerConnectionId === assignModeConnectionId) {
        return '#00bcd4'; // Cyan for selected connection's cells
      }
    }

    if (assignment) {
      // Use different colors for offline players vs connections
      if (assignment.playerConnectionId) {
        return '#e91e63'; // Pink for connection assignments
      }
      return '#9c27b0'; // Purple for offline player assignments
    }

    return '#ffffff'; // White for unassigned
  };

  const getCellLabel = (cellNumber: number): { icon?: React.ReactNode; handle?: string } => {
    const assignment = getCellAssignment(cellNumber);

    if (assignment?.offlinePlayer) {
      const icon = assignment.offlinePlayer.iconName ? renderIcon(assignment.offlinePlayer.iconName, 18) : null;
      const handle = assignment.offlinePlayer.handle?.toUpperCase() || assignment.offlinePlayer.displayName.substring(0, 3).toUpperCase();
      return { icon: icon || undefined, handle };
    }

    // Handle connection assignments
    if (assignment?.playerConnection) {
      const conn = assignment.playerConnection;
      // Connections are to online users
      if (conn.connectedUser) {
        const user = conn.connectedUser;
        const handle = user.handle?.toUpperCase() || user.displayName?.substring(0, 3).toUpperCase() || '???';
        // Use a link icon to differentiate from offline players
        return { icon: renderIcon('Link', 16), handle };
      }
      // Fallback to nickname if available
      if (conn.nickname) {
        return { icon: renderIcon('Link', 16), handle: conn.nickname.substring(0, 3).toUpperCase() };
      }
    }

    // Show cell number for unassigned cells
    return { handle: cellNumber.toString() };
  };

  const getCellTooltip = (cellNumber: number): string => {
    const assignment = getCellAssignment(cellNumber);

    if (assignment?.offlinePlayer) {
      const handle = assignment.offlinePlayer.handle ? ` (@${assignment.offlinePlayer.handle.toUpperCase()})` : '';
      return `${assignment.offlinePlayer.displayName}${handle}`;
    }

    // Handle connection assignments
    if (assignment?.playerConnection) {
      const conn = assignment.playerConnection;
      if (conn.connectedUser) {
        const user = conn.connectedUser;
        const handle = user.handle ? ` (@${user.handle.toUpperCase()})` : '';
        const nickname = conn.nickname ? ` "${conn.nickname}"` : '';
        return `${user.displayName || 'User'}${handle}${nickname} (Connected)`;
      }
      if (conn.nickname) {
        return `${conn.nickname} (Connected)`;
      }
      return 'Connected Player';
    }

    return '';
  };

  const getPlayerOptions = (): PlayerOption[] => {
    return offlinePlayers.map((op) => ({
      id: op.id,
      displayName: op.displayName,
      handle: op.handle,
      iconName: op.iconName,
    })).sort((a, b) => a.displayName.localeCompare(b.displayName));
  };

  const handleCellClick = (cellNumber: number) => {
    const assignment = getCellAssignment(cellNumber);
    const [row, col] = getCellRepresentativeSquare(cellNumber, cellMode, cellShape);

    // If assign mode is enabled, bypass the dialog
    if (assignModeEnabled && onSquareClick) {
      const isSelectedPlayerCell = assignModePlayerId && assignment?.offlinePlayerId === assignModePlayerId;
      const isSelectedConnectionCell = assignModeConnectionId && assignment?.playerConnectionId === assignModeConnectionId;
      const isEmpty = !assignment;

      if (isEmpty) {
        // Empty cell - assign it
        onSquareClick(row, col);
      } else if (isSelectedPlayerCell || isSelectedConnectionCell) {
        // Selected player's/connection's cell - unassign it
        onClear(row, col);
      }
      return;
    }

    // Normal mode - open dialog
    setSelectedCell(cellNumber);
    if (assignment?.offlinePlayerId) {
      const player = offlinePlayers.find(p => p.id === assignment.offlinePlayerId);
      if (player) {
        setSelectedPlayer({
          id: player.id,
          displayName: player.displayName,
          handle: player.handle,
          iconName: player.iconName,
        });
      } else {
        setSelectedPlayer(null);
      }
    } else {
      setSelectedPlayer(null);
    }
    setAssignDialogOpen(true);
  };

  const handleAssign = async () => {
    if (selectedCell && selectedPlayer) {
      const [row, col] = getCellRepresentativeSquare(selectedCell, cellMode, cellShape);
      await onAssign(row, col, selectedPlayer.id);
      setAssignDialogOpen(false);
      setSelectedCell(null);
      setSelectedPlayer(null);
    }
  };

  const handleClear = async () => {
    if (selectedCell) {
      const [row, col] = getCellRepresentativeSquare(selectedCell, cellMode, cellShape);
      await onClear(row, col);
      setAssignDialogOpen(false);
      setSelectedCell(null);
      setSelectedPlayer(null);
    }
  };

  const isCellAssigned = selectedCell ? !!getCellAssignment(selectedCell) : false;

  // Calculate cell size based on mode
  // For paired modes, cells are rectangular (2 squares combined)
  // For quad mode, cells are square (4 squares combined in 2x2)
  const baseCellSize = 50;
  let cellWidth = baseCellSize;
  let cellHeight = baseCellSize;

  if (cellMode === 'quad') {
    // 2x2 cells - square, double size
    cellWidth = baseCellSize * 2;
    cellHeight = baseCellSize * 2;
  } else if (cellMode === 'paired') {
    if (cellShape === '2x1') {
      // Vertical pairs: 1 wide, 2 tall
      cellWidth = baseCellSize;
      cellHeight = baseCellSize * 2;
    } else {
      // Horizontal pairs (1x2): 2 wide, 1 tall
      cellWidth = baseCellSize * 2;
      cellHeight = baseCellSize;
    }
  }

  const gap = 4;
  const playerOptions = getPlayerOptions();

  return (
    <Box>
      <Paper sx={{ p: 2, overflow: 'auto' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${cols}, ${cellWidth}px)`,
            gridTemplateRows: `repeat(${rows}, ${cellHeight}px)`,
            gap: `${gap}px`,
            width: 'fit-content',
          }}
        >
          {Array.from({ length: totalCells }, (_, index) => {
            const cellNumber = index + 1;
            const bgColor = getCellColor(cellNumber);
            const { icon, handle } = getCellLabel(cellNumber);
            const tooltip = getCellTooltip(cellNumber);
            const isAssigned = !!getCellAssignment(cellNumber);

            const cell = (
              <Box
                key={cellNumber}
                onClick={() => handleCellClick(cellNumber)}
                sx={{
                  width: cellWidth,
                  height: cellHeight,
                  backgroundColor: bgColor,
                  border: '2px solid',
                  borderColor: 'rgba(0, 0, 0, 0.2)',
                  borderRadius: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  '&:hover': {
                    transform: 'scale(1.03)',
                    boxShadow: 2,
                    zIndex: 1,
                  },
                  color: isAssigned ? 'white' : 'text.secondary',
                }}
              >
                {icon}
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: cellMode === 'quad' ? 14 : icon ? 9 : 10,
                    fontWeight: isAssigned ? 600 : 400,
                    lineHeight: 1,
                    mt: icon ? 0.25 : 0,
                  }}
                >
                  {handle}
                </Typography>
              </Box>
            );

            return tooltip ? (
              <Tooltip key={cellNumber} title={tooltip} arrow placement="top">
                {cell}
              </Tooltip>
            ) : (
              cell
            );
          })}
        </Box>
      </Paper>

      {/* Assignment Dialog */}
      <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {isCellAssigned ? 'Edit Cell Assignment' : 'Assign Cell'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {selectedCell && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Cell #{selectedCell}
              </Typography>
            )}
            <Autocomplete
              options={playerOptions}
              getOptionLabel={(option) => option.displayName}
              value={selectedPlayer}
              onChange={(_, newValue) => setSelectedPlayer(newValue)}
              renderInput={(params) => (
                <TextField {...params} label="Select Player" placeholder="Search players..." />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {option.iconName && renderIcon(option.iconName, 20)}
                  <span>{option.displayName}</span>
                  {option.handle && (
                    <Typography variant="caption" color="text.secondary">
                      @{option.handle.toUpperCase()}
                    </Typography>
                  )}
                </Box>
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          {isCellAssigned && (
            <Button onClick={handleClear} color="error">
              Clear
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAssign} variant="contained" disabled={!selectedPlayer}>
            Assign
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
